import { LoadingDisplay, DatePickerWithArrows } from 'components/molecules';
import { AppGoBackButton, AppText, TopAppMenuProps } from 'components/atoms';
import { settlementTexts, commonTexts } from 'consts/text/index';
import { useDateFilter } from 'hooks/useDateFilter/useDateFilter';
import { DATE_FILTER_KEY } from 'consts/filters/common/filtersKeys';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { parseStringIntoDayjsDate } from 'utils/parsers/dateTime/parseStringIntoDayjsDate';
import dayjs from 'dayjs';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { useOutlet, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ChildId } from 'types';
import { appRoutes } from 'consts/paths/paths';
import { BalanceTag } from 'components/molecules/BalanceTag/BalanceTag';
import { Row, Col, Dropdown, MenuProps } from 'antd';
import { getMonthFromDayjsMonth } from 'utils/getMonthFromDayjsMonth';
import ParentHTTPService from 'services/HTTPService/parent/ParentHTTPService';
import { ParentSettlementCostSplitList } from 'components/templates/settlement/ParentSettlementCostSplitList/ParentSettlementCostSplitList';
import { ParentTransactions } from 'components/templates/settlement/ParentTransactions/ParentTransactions';
import { StyledAddButton } from 'pages/app/parent/groupAttendance/ParentGroupAttendancePage/ParentGroupAttendancePage.styled';
import { IconCash } from '@tabler/icons-react';
import { StyledAppMenu, StyledRow } from '../ChildSettlementDetails/ChildSettlementDetails.styled';
import { If } from '../../../atoms/If/If';
import {
    ChildSettlementDetailsMenuItems,
    ChildSettlementDetailsTabValue,
    ChildSettlementDetailsUrlSearchKey,
} from '../ChildSettlementDetails/ChildSettlementDetails.utils';
import { ParentSettlementMonthlyList } from './ParentSettlementMonthlyList/ParentSettlementMonthlyList';
import { ParentSettlementsCorrections } from './ParentSettlementsCorrections/ParentSettlementsCorrections';
import { ParentInvoices } from '../ParentInvoices/ParentInvoices';
import { ParentAdditionalCostList } from '../ParentAdditionalCostList/ParentAdditionalCostList';

export const ParentSettlementDetails = () => {
    const outlet = useOutlet();
    const navigate = useNavigate();
    const { displayError } = useSimpleNotification();

    const pageRoute = appRoutes.app.settlement.children.pickedChild.base;
    const [searchParams, setSearchParams] = useSearchParams();

    const [isPaymentLoading, setIsPaymentLoading] = useState(false);

    const { dateFilterValue, handleChangeDateFilter, handleClickNextOrPrevDate } = useDateFilter({
        filterKey: DATE_FILTER_KEY,
    });

    const webParams = new URLSearchParams(window.location.search);
    const settlementType = webParams.get(
        ChildSettlementDetailsUrlSearchKey,
    ) as ChildSettlementDetailsTabValue;

    const selectedDate = dateFilterValue ? parseStringIntoDayjsDate(dateFilterValue) : dayjs();
    const selectedYear = selectedDate.year();
    const selectedMonth = getMonthFromDayjsMonth(selectedDate.month());
    const selectMenuKey: TopAppMenuProps['onClick'] = (menuInfo) => {
        const params = new URLSearchParams(window.location.search);
        params.set(ChildSettlementDetailsUrlSearchKey, menuInfo.key);
        setSearchParams(params, { replace: true });
    };
    const period = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`;

    const { data: childBalances, isFetching: isChildBalancesFetching } = useAppQuery(
        'CHILD_SETTLEMENT_BALANCE',
        [period],
        () => ParentHTTPService.settlements.getChildSettlementBalance({ yearMonth: period }),
        {},
    );

    const actualChildId = childBalances?.childId as ChildId | undefined;
    const { data: childProfileData, isInitialLoading: isChildProfileLoading } = useAppQuery(
        'CHILD',
        [actualChildId],
        () => ParentHTTPService.children.getChild(actualChildId!),
        { enabled: !!actualChildId },
    );

    const institutionId = childProfileData?.institutionId;

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (params.get(ChildSettlementDetailsUrlSearchKey)) return;
        if (params.get(pageRoute)) return;

        params.set(ChildSettlementDetailsUrlSearchKey, 'MONTHLY_SETTLEMENT');

        setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePrzelewClick = (): void => {
        if (!actualChildId || !institutionId) {
            displayError('Brak danych dziecka lub instytucji.');
            return;
        }

        navigate(appRoutes.app.parent.settlement.payment, {
            state: {
                childId: actualChildId,
                institutionId,
            },
        });
    };

    const handlePayUClick = async (): Promise<void> => {
        if (!actualChildId || !institutionId) {
            displayError('Brak danych dziecka lub instytucji.');
            return;
        }

        try {
            setIsPaymentLoading(true);
            const response = await ParentHTTPService.bills.getPaymentRedirect(
                actualChildId as string,
                institutionId as string,
            );

            if (response.data.redirectUri) {
                window.location.href = response.data.redirectUri;
            } else {
                displayError('Brak linku do płatności PayU.');
            }
        } catch (error) {
            displayError('Wystąpił błąd podczas inicjowania płatności PayU.');
        } finally {
            setIsPaymentLoading(false);
        }
    };

    const paymentMenuItems: MenuProps['items'] = [
        { key: 'payu', label: 'PayU', onClick: handlePayUClick },
        { key: 'przelew', label: 'Przelew tradycyjny', onClick: handlePrzelewClick },
    ];

    const isMonthlySettlement =
        searchParams.get(ChildSettlementDetailsUrlSearchKey) === 'MONTHLY_SETTLEMENT';

    if (isChildBalancesFetching) {
        return <LoadingDisplay />;
    }

    return (
        <>
            {outlet}
            <Row justify="space-between" align="middle">
                <AppGoBackButton />
            </Row>
            <Row align="middle" gutter={[0, 16]} style={{ marginBottom: '16px', width: '100%' }}>
                <Col xs={24} md={{ flex: 'auto' }}>
                    <StyledRow style={{ flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                        <AppText marginLeft={0} textType="H5">
                            {settlementTexts.children.details.balances}
                        </AppText>
                        <AppText marginLeft={0}>
                            {settlementTexts.children.details.commonBalance}
                        </AppText>
                        <BalanceTag amount={childBalances?.generalBalance || 0} />
                        <AppText marginLeft={0}>
                            {settlementTexts.children.details.parentBalance}
                        </AppText>
                        <BalanceTag amount={childBalances?.parentBalance || 0} />
                        <AppText marginLeft={0}>
                            {settlementTexts.children.details.foundationBalance}
                        </AppText>
                        <BalanceTag amount={childBalances?.foundationBalance || 0} />
                        <AppText marginLeft={0}>
                            {settlementTexts.children.details.cateringBalance}
                        </AppText>
                        <BalanceTag amount={childBalances?.cateringBalance || 0} />
                    </StyledRow>
                </Col>

                {childBalances && childBalances.parentBalance < 0 && (
                    <Col
                        xs={24}
                        md={{ flex: 'none' }}
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                        <Dropdown
                            menu={{ items: paymentMenuItems }}
                            trigger={['click']}
                            disabled={isPaymentLoading || !institutionId || isChildProfileLoading}
                        >
                            <div style={{ width: '100%', cursor: 'pointer' }}>
                                <StyledAddButton
                                    addButtonLabel="Opłać"
                                    icon={<IconCash size={16} />}
                                />
                            </div>
                        </Dropdown>
                    </Col>
                )}
            </Row>
            <ParentTransactions />
            <StyledAppMenu
                mode="horizontal"
                selectedKeys={[settlementType]}
                items={ChildSettlementDetailsMenuItems}
                onClick={selectMenuKey}
            />
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    width: '100%',
                    marginBottom: '8px',
                }}
            >
                <DatePickerWithArrows
                    picker="month"
                    format="MMMM"
                    label={commonTexts.actionLabels.selectMonth}
                    value={selectedDate}
                    onChange={handleChangeDateFilter}
                    onArrowButtonClick={(arrowButtonType) => {
                        if (arrowButtonType === 'NEXT') {
                            handleClickNextOrPrevDate?.('NEXT', 'month');
                        } else if (arrowButtonType === 'PREVIOUS') {
                            handleClickNextOrPrevDate?.('PREVIOUS', 'month');
                        }
                    }}
                />
            </div>
            {actualChildId && (
                <>
                    <If condition={isMonthlySettlement}>
                        <ParentSettlementsCorrections
                            selectedMonth={selectedMonth}
                            selectedYear={selectedYear}
                            childId={actualChildId}
                        />
                        <ParentSettlementMonthlyList period={period} childId={actualChildId} />
                        <ParentInvoices period={period} childId={actualChildId} />
                        <ParentSettlementCostSplitList
                            title={settlementTexts.children.costAllocation.title}
                            period={period}
                            childId={actualChildId}
                        />
                    </If>
                    <If condition={!isMonthlySettlement}>
                        <ParentAdditionalCostList period={period} childId={actualChildId} />
                    </If>
                </>
            )}
        </>
    );
};
